<template>
  <div
    class="product-card"
    :class="computedClass"
    tabindex="0"
    role="button"
    :aria-disabled="!isClickable"
    @click="emitOnclick"
    @keydown.enter.space="emitOnclick"
  >
    <BaseText
      class="product-card__title"
      :color="NebraskaColors.textPrimary"
      size="md"
    >
      {{ productName }}
    </BaseText>
    <div class="product-card__row">
      <BaseText :color="NebraskaColors.textSecondary" size="sm">
        Percentual de alocação
      </BaseText>
      <BaseText size="sm">
        {{ productAllocation }}
      </BaseText>
    </div>
    <div v-if="productTotalBalance" class="product-card__row">
      <BaseText :color="NebraskaColors.textSecondary" size="sm">
        Saldo bruto
      </BaseText>
      <BaseText :color="NebraskaColors.textPrimary" size="sm">
        <Money>{{ productTotalBalance }}</Money>
      </BaseText>
    </div>
    <div v-if="productPerformanceIsNumber" class="product-card__row">
      <BaseText :color="NebraskaColors.textSecondary" size="sm">
        Rentabilidade em carteira
      </BaseText>
      <BaseText
        v-if="shouldShowMoney"
        :color="NebraskaColors.textPrimary"
        size="sm"
      >
        {{ formattedPerformancePercentage(productPerformance) }}
      </BaseText>
      <span v-else class="wrapper__placeholder" />
    </div>
    <div v-if="referenceValueDate" class="product-card__row">
      <BaseText :color="NebraskaColors.textSecondary" size="sm">
        Valor de mercado em
      </BaseText>
      <BaseText v-if="shouldShowMoney" size="sm">
        {{ referenceValueDate }}
      </BaseText>
      <span v-else class="wrapper__placeholder" />
    </div>
  </div>
</template>

<script lang="ts" scoped>
import { Component, Prop, Vue, Emit } from 'vue-property-decorator';

import { BaseText } from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { Money } from '@/modules/common/components/Money';

import {
  percentageWithSign,
  percentageTruncate
} from '@/modules/common/helpers/percentage';
import { useDashStore } from '@/modules/common/store/DashModule';

@Component({
  components: { BaseText, Money }
})
export default class ProductAllocationCard extends Vue {
  private readonly dashModule = useDashStore();
  public readonly NebraskaColors = NebraskaColors;

  @Prop({ type: String, required: true })
  private productName!: string;

  @Prop({ type: String, required: true })
  private productAllocation!: string;

  @Prop({ type: String, required: false })
  private productTotalBalance?: string;

  @Prop({ type: Number, required: false })
  private productPerformance?: number;

  @Prop({ type: String, required: false })
  private referenceValueDate?: string;

  @Prop({ type: Boolean, default: true })
  private isClickable!: string;

  public formattedPerformancePercentage(value: number): string {
    return percentageWithSign(percentageTruncate(value, 2));
  }

  public getPerformanceTextColor(value: number): NebraskaColors {
    return value >= 0
      ? NebraskaColors.monetaryPositive
      : NebraskaColors.monetaryNegative;
  }

  private get productPerformanceIsNumber() {
    return typeof this.productPerformance === 'number';
  }

  private get shouldShowMoney() {
    return this.dashModule.showMoney;
  }

  get computedClass() {
    return this.isClickable ? '' : 'unclickable-card';
  }

  @Emit()
  private onclick() {
    return () => {};
  }

  emitOnclick() {
    if (!this.isClickable) {
      return;
    }

    this.onclick();
  }
}
</script>

<style lang="less" scoped>
.product-card {
  background: @background-secondary;
  width: 100%;
  padding: @size-spacing-x400;
  cursor: pointer;
  border: solid 1px @divider-primary;
  border-radius: 8px;
  margin-bottom: @size-spacing-x300;

  &__row {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding-bottom: @size-spacing-x150;
  }
  &__title {
    padding-bottom: @size-spacing-x300;
  }

  &:hover {
    background: @background-primary;
  }
}
.wrapper__placeholder {
  background: @element-secondary;
  border-radius: 4px;
  width: 60px;
  height: 16px;
  margin-top: @size-spacing-x150;
}

.unclickable-card {
  cursor: not-allowed;
  &:hover {
    background: @background-secondary;
  }
}
</style>
